export interface QrmarkScanResult {
  error: string;
  found: boolean;
  ms: number;
  qrText: string;
  serial: string;
  source: string;
}

export interface QrmarkScannerSupport {
  reason: string;
  supported: boolean;
}

interface PendingScan {
  reject: (error: Error) => void;
  resolve: (result: QrmarkScanResult) => void;
  timer: number;
}

interface WorkerMessage {
  error?: string;
  found?: boolean;
  id?: number;
  ms?: number;
  qrText?: string;
  serial?: string;
  source?: string;
  type?: string;
  variant?: string;
}

const INIT_TIMEOUT_MS = 15000;
const SCAN_TIMEOUT_MS = 12000;

const WORKER_SOURCE = `
let ex = null, ready = false, rgbaPtr = 0, rgbaCap = 0, textPtr = 0;
const PATH = ['快路径', '自研兜底', '兜底+自解码'];
const BULK_TEST = new Uint8Array([0,97,115,109,1,0,0,0,1,4,1,96,0,0,3,2,1,0,5,3,1,0,1,10,14,1,12,0,65,0,65,0,65,0,252,10,0,0,11]);

function u8() {
  return new Uint8Array(ex.memory.buffer);
}

function decodeText(memory, pointer, capacity) {
  let length = 0;
  while (length < capacity && memory[pointer + length] !== 0) length += 1;
  const bytes = memory.subarray(pointer, pointer + length);
  try {
    return new TextDecoder().decode(bytes);
  } catch (_) {
    let text = '';
    for (let i = 0; i < bytes.length; i += 1) text += String.fromCharCode(bytes[i]);
    return text;
  }
}

async function instantiateFrom(url) {
  const response = await fetch(url, { cache: 'force-cache' });
  if (!response.ok) throw new Error('算法文件加载失败：' + response.status);
  const result = await WebAssembly.instantiate(await response.arrayBuffer(), {});
  return result.instance;
}

self.onmessage = async (event) => {
  const message = event.data;
  try {
    if (message.type === 'init') {
      let modern = false;
      try { modern = WebAssembly.validate(BULK_TEST); } catch (_) {}
      let instance;
      let variant = modern ? 'fast' : 'mvp';
      try {
        instance = await instantiateFrom(modern ? message.fastUrl : message.mvpUrl);
      } catch (fastError) {
        if (!modern) throw fastError;
        instance = await instantiateFrom(message.mvpUrl);
        variant = 'mvp';
      }
      ex = instance.exports;
      textPtr = ex.wasm_alloc(512);
      ready = true;
      self.postMessage({ type: 'ready', variant });
      return;
    }

    if (message.type === 'reset') {
      if (ready && ex.wasm_reset) ex.wasm_reset();
      return;
    }

    if (message.type === 'setHint') {
      if (ready && ex.wasm_set_hint) {
        const bytes = new TextEncoder().encode(message.text || '');
        const pointer = ex.wasm_alloc(bytes.length || 1);
        u8().set(bytes, pointer);
        ex.wasm_set_hint(pointer, bytes.length);
      }
      return;
    }

    if (message.type === 'scan') {
      if (!ready) throw new Error('识别引擎尚未就绪');
      const rgba = new Uint8Array(message.buffer);
      if (rgba.length > rgbaCap) {
        rgbaPtr = ex.wasm_alloc(rgba.length);
        rgbaCap = rgba.length;
      }
      u8().set(rgba, rgbaPtr);
      u8().fill(0, textPtr, textPtr + 512);
      const startedAt = performance.now();
      const serial = ex.wasm_pipeline(rgbaPtr, message.width, message.height, textPtr);
      const ms = Math.round(performance.now() - startedAt);
      const pathId = ex.wasm_last_path ? ex.wasm_last_path() : -1;
      const qrText = decodeText(u8(), textPtr, 512);
      const serialText = String(serial);
      const found = serialText.charAt(0) !== '-';
      self.postMessage({
        type: 'result',
        id: message.id,
        found,
        serial: found ? serialText : '',
        qrText,
        source: PATH[pathId] || '',
        ms,
        error: !found && !qrText ? '未定位到二维码' : '',
      });
    }
  } catch (error) {
    self.postMessage({
      type: message.type === 'init' ? 'error' : 'result',
      id: message.id,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};
`;

function getAssetUrl(fileName: string) {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return new URL(`${publicUrl}/qrmark/v1/${fileName}`, window.location.href).href;
}

function getVideoSize(video: HTMLVideoElement) {
  return {
    height: video.videoHeight || video.clientHeight,
    width: video.videoWidth || video.clientWidth,
  };
}

function getCenteredSquare(size: { height: number; width: number }) {
  const side = Math.min(size.width, size.height);
  return {
    height: side,
    width: side,
    x: Math.max(0, Math.round((size.width - side) / 2)),
    y: Math.max(0, Math.round((size.height - side) / 2)),
  };
}

export function getQrmarkScannerSupport(): QrmarkScannerSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { supported: false, reason: '当前环境不是浏览器' };
  }
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    return {
      supported: false,
      reason: window.isSecureContext
        ? '当前浏览器未开放摄像头能力'
        : '摄像头扫码需要可信 HTTPS；PDA WebView 还需授权摄像头',
    };
  }
  if (typeof Worker === 'undefined' || typeof WebAssembly === 'undefined') {
    return { supported: false, reason: '当前 PDA 浏览器不支持 Web Worker 或 WebAssembly' };
  }
  if (typeof BigInt !== 'function') {
    return { supported: false, reason: '当前 PDA 浏览器版本过低，不支持隐写码识别' };
  }
  return { supported: true, reason: '' };
}

export class QrmarkScanner {
  readonly ready: Promise<void>;

  variant = '';

  private readonly canvas: HTMLCanvasElement;

  private readonly context: CanvasRenderingContext2D;

  private destroyed = false;

  private initReject: ((error: Error) => void) | null = null;

  private initResolve: (() => void) | null = null;

  private initTimer = 0;

  private nextId = 0;

  private readonly pending = new Map<number, PendingScan>();

  private readonly worker: Worker;

  constructor() {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('当前浏览器无法创建图像处理画布');
    }
    this.canvas = canvas;
    this.context = context;

    const workerUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    this.worker = new Worker(workerUrl);
    URL.revokeObjectURL(workerUrl);

    this.ready = new Promise<void>((resolve, reject) => {
      this.initResolve = resolve;
      this.initReject = reject;
    });
    this.initTimer = window.setTimeout(() => {
      this.fail(new Error('识别引擎加载超时'));
    }, INIT_TIMEOUT_MS);

    this.worker.addEventListener('message', this.handleMessage);
    this.worker.addEventListener('error', this.handleWorkerError);
    this.worker.addEventListener('messageerror', this.handleWorkerMessageError);
    this.worker.postMessage({
      type: 'init',
      fastUrl: getAssetUrl('qrmark.fast.wasm.js'),
      mvpUrl: getAssetUrl('qrmark.mvp.wasm.js'),
    });
  }

  async scan(video: HTMLVideoElement, maxSide = 1440): Promise<QrmarkScanResult> {
    await this.ready;
    if (this.destroyed) {
      throw new Error('识别引擎已释放');
    }

    const size = getVideoSize(video);
    if (!size.width || !size.height) {
      throw new Error('摄像头画面尚未就绪');
    }

    // 取景框通过 object-fit: cover 展示视频中央的方形区域。旧实现却把整张竖屏
    // 画面压缩后送进 WASM，PDA 的 1080×1920 视频会先被缩成 810×1440，既浪费
    // 算力分析不可见区域，也降低了取景框内隐写码的有效像素。这里按用户实际看到
    // 的区域裁切，保证手机、电脑和竖屏 PDA 使用同一份输入画面。
    const crop = getCenteredSquare(size);
    const scale = maxSide > 0 && crop.width > maxSide
      ? maxSide / crop.width
      : 1;
    const width = Math.max(1, Math.round(crop.width * scale));
    const height = Math.max(1, Math.round(crop.height * scale));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.context.imageSmoothingEnabled = true;
    this.context.imageSmoothingQuality = 'high';
    this.context.drawImage(
      video,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      width,
      height,
    );
    const imageData = this.context.getImageData(0, 0, width, height);
    const buffer = imageData.data.buffer as ArrayBuffer;
    const id = this.nextId += 1;

    return new Promise<QrmarkScanResult>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('本帧识别超时'));
      }, SCAN_TIMEOUT_MS);
      this.pending.set(id, { reject, resolve, timer });
      try {
        this.worker.postMessage({ type: 'scan', id, width, height, buffer }, [buffer]);
      } catch (error) {
        window.clearTimeout(timer);
        this.pending.delete(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  reset() {
    if (!this.destroyed) {
      this.worker.postMessage({ type: 'reset' });
    }
  }

  setHint(text: string) {
    if (!this.destroyed) {
      this.worker.postMessage({ type: 'setHint', text });
    }
  }

  destroy() {
    this.shutdown(new Error('识别引擎已释放'));
  }

  private readonly handleMessage = (event: MessageEvent<WorkerMessage>) => {
    const message = event.data;
    if (message.type === 'ready') {
      window.clearTimeout(this.initTimer);
      this.variant = message.variant || '';
      this.initResolve?.();
      this.initResolve = null;
      this.initReject = null;
      return;
    }
    if (message.type === 'error') {
      this.fail(new Error(message.error || '识别引擎初始化失败'));
      return;
    }
    if (message.type !== 'result' || typeof message.id !== 'number') {
      return;
    }
    const pending = this.pending.get(message.id);
    if (!pending) {
      return;
    }
    window.clearTimeout(pending.timer);
    this.pending.delete(message.id);
    pending.resolve({
      error: message.error || '',
      found: Boolean(message.found),
      ms: message.ms || 0,
      qrText: message.qrText || '',
      serial: message.serial || '',
      source: message.source || '',
    });
  };

  private readonly handleWorkerError = (event: ErrorEvent) => {
    this.fail(new Error(event.message || '识别 Worker 运行失败'));
  };

  private readonly handleWorkerMessageError = () => {
    this.fail(new Error('识别 Worker 消息解析失败'));
  };

  private fail(error: Error) {
    this.shutdown(error);
  }

  private shutdown(error: Error) {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    window.clearTimeout(this.initTimer);
    this.worker.removeEventListener('message', this.handleMessage);
    this.worker.removeEventListener('error', this.handleWorkerError);
    this.worker.removeEventListener('messageerror', this.handleWorkerMessageError);
    this.worker.terminate();
    this.initReject?.(error);
    this.initReject = null;
    this.initResolve = null;
    this.pending.forEach((pending) => {
      window.clearTimeout(pending.timer);
      pending.reject(error);
    });
    this.pending.clear();
  }
}

let scannerPromise: Promise<QrmarkScanner> | null = null;

export function getQrmarkScanner() {
  if (!scannerPromise) {
    scannerPromise = Promise.resolve()
      .then(() => {
        const support = getQrmarkScannerSupport();
        if (!support.supported) {
          throw new Error(support.reason);
        }
        return new QrmarkScanner();
      })
      .then(async (scanner) => {
        await scanner.ready;
        return scanner;
      })
      .catch((error) => {
        scannerPromise = null;
        throw error;
      });
  }
  return scannerPromise;
}

export function releaseQrmarkScanner() {
  const activeScanner = scannerPromise;
  scannerPromise = null;
  void activeScanner?.then((scanner) => scanner.destroy()).catch(() => undefined);
}
